import { useMemo, useState } from "react";
import {
  AudiencesClient,
  type AudienceExpression,
  type AudiencePreview,
  type AudienceSchemaDescription,
  type AudienceTerm,
} from "../api/generated";
import { Alert, Chip, Field, describeError } from "./ui";

const audiences = new AudiencesClient("");

type Clause = "include" | "require" | "exclude";

/**
 * The audience builder (§10). Nobody composes JSON in a UI — a national-office user picks from
 * THIS tenant's attributes, groups and hierarchy nodes, all generated from the schema endpoint.
 * The same component serves the Audiences module and Compose, so a group definition and a
 * broadcast are built the same way.
 */
export function AudienceBuilder({
  schema,
  value,
  onChange,
  channels,
  onChannelsChange,
}: {
  schema: AudienceSchemaDescription;
  value: AudienceExpression;
  onChange: (next: AudienceExpression) => void;
  channels?: string[];
  onChannelsChange?: (next: string[]) => void;
}) {
  const [kind, setKind] = useState<string>("where");
  const [clause, setClause] = useState<Clause>("include");
  const [attributeKey, setAttributeKey] = useState<string>(schema.attributes?.[0]?.key ?? "");
  const [attributeValues, setAttributeValues] = useState<string>("");
  const [groupKey, setGroupKey] = useState<string>(schema.groups?.[0]?.key ?? "");
  const [nodeKey, setNodeKey] = useState<string>(schema.nodes?.[0]?.key ?? "");
  const [partyType, setPartyType] = useState<string>("store");
  const [partyKeys, setPartyKeys] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const definition = useMemo(
    () => schema.attributes?.find((a) => a.key === attributeKey),
    [schema.attributes, attributeKey],
  );

  function addTerm() {
    setError(null);
    let term: AudienceTerm;

    switch (kind) {
      case "all":
        term = { all: { party_type: partyType } };
        break;
      case "where": {
        const values = attributeValues.split(",").map((v) => v.trim()).filter(Boolean);
        if (!attributeKey || values.length === 0) {
          setError("Pick an attribute and at least one value.");
          return;
        }
        term = { where: { [attributeKey]: values } };
        break;
      }
      case "group":
        if (!groupKey) { setError("No groups are defined yet."); return; }
        term = { group: groupKey };
        break;
      case "under":
        if (!nodeKey) { setError("This tenant has no hierarchy."); return; }
        term = { under: nodeKey };
        break;
      case "parties": {
        const keys = partyKeys.split(",").map((v) => v.trim()).filter(Boolean);
        if (keys.length === 0) { setError("List at least one party key."); return; }
        term = { parties: keys };
        break;
      }
      default:
        return;
    }

    onChange({ ...value, [clause]: [...(value[clause] ?? []), term] });
  }

  function removeTerm(from: Clause, index: number) {
    onChange({ ...value, [from]: (value[from] ?? []).filter((_, i) => i !== index) });
  }

  return (
    <div>
      {error && <Alert kind="error">{error}</Alert>}

      <div className="row" style={{ marginBottom: 12 }}>
        <Field label="Clause" style={{ maxWidth: 120 }}>
          <select value={clause} onChange={(e) => setClause(e.target.value as Clause)}>
            <option value="include">include (or)</option>
            <option value="require">require (and)</option>
            <option value="exclude">exclude (not)</option>
          </select>
        </Field>

        <Field label="Term kind" style={{ maxWidth: 140 }}>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="where">attribute</option>
            <option value="all">whole party type</option>
            <option value="group">named audience</option>
            <option value="under">hierarchy node</option>
            <option value="parties">explicit parties</option>
          </select>
        </Field>

        {kind === "where" && (
          <>
            <Field label="Attribute" style={{ maxWidth: 170 }}>
              <select value={attributeKey} onChange={(e) => { setAttributeKey(e.target.value); setAttributeValues(""); }}>
                {schema.attributes?.map((a) => (
                  <option key={a.key} value={a.key}>{a.displayName} ({a.key})</option>
                ))}
              </select>
            </Field>
            <Field label={`Values ${definition?.allowedValues ? `(${definition.allowedValues.join(", ")})` : "(comma separated)"}`}>
              {definition?.allowedValues ? (
                <select
                  multiple
                  size={Math.min(4, definition.allowedValues.length)}
                  value={attributeValues.split(",").map((v) => v.trim()).filter(Boolean)}
                  onChange={(e) =>
                    setAttributeValues([...e.target.selectedOptions].map((o) => o.value).join(","))
                  }
                >
                  {definition.allowedValues.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input value={attributeValues} onChange={(e) => setAttributeValues(e.target.value)} placeholder="on, qc" />
              )}
            </Field>
          </>
        )}

        {kind === "all" && (
          <Field label="Party type" style={{ maxWidth: 160 }}>
            <select value={partyType} onChange={(e) => setPartyType(e.target.value)}>
              {schema.partyTypes?.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        )}

        {kind === "group" && (
          <Field label="Named audience">
            <select value={groupKey} onChange={(e) => setGroupKey(e.target.value)}>
              {schema.groups?.map((g) => (
                <option key={g.key} value={g.key}>{g.name} — {g.memberCount} members</option>
              ))}
            </select>
          </Field>
        )}

        {kind === "under" && (
          <Field label="Hierarchy node">
            <select value={nodeKey} onChange={(e) => setNodeKey(e.target.value)}>
              {schema.nodes?.map((n) => (
                <option key={n.key} value={n.key}>{"— ".repeat(n.depth ?? 0)}{n.name}</option>
              ))}
            </select>
          </Field>
        )}

        {kind === "parties" && (
          <Field label="Party keys (comma separated)">
            <input value={partyKeys} onChange={(e) => setPartyKeys(e.target.value)} placeholder="p_118, p_204" />
          </Field>
        )}

        <button type="button" className="ghost" onClick={addTerm}>Add term</button>
      </div>

      <TermList clause="include" terms={value.include} onRemove={removeTerm} />
      <TermList clause="require" terms={value.require} onRemove={removeTerm} />
      <TermList clause="exclude" terms={value.exclude} onRemove={removeTerm} />

      {onChannelsChange && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
          <label>
            Channels — which of a recipient's systems receive this. Leave empty for all.
          </label>
          <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
            {schema.channels?.map((c) => (
              <div className="checkbox" key={c}>
                <input
                  id={`ch-${c}`}
                  type="checkbox"
                  checked={(channels ?? []).includes(c)}
                  onChange={(e) =>
                    onChannelsChange(
                      e.target.checked
                        ? [...(channels ?? []), c]
                        : (channels ?? []).filter((x) => x !== c),
                    )
                  }
                />
                <label htmlFor={`ch-${c}`}>{c}</label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TermList({ clause, terms, onRemove }: {
  clause: Clause;
  terms?: AudienceTerm[];
  onRemove: (clause: Clause, index: number) => void;
}) {
  if (!terms || terms.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <label>{clause}</label>
      {terms.map((term, index) => (
        <div key={index} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <span className="mono-block" style={{ flex: 1, padding: "6px 9px" }}>{describeTerm(term)}</span>
          <button type="button" className="ghost small" onClick={() => onRemove(clause, index)}>remove</button>
        </div>
      ))}
    </div>
  );
}

export function describeTerm(term: AudienceTerm): string {
  if (term.all) return `all ${term.all.party_type}`;
  if (term.where) {
    return Object.entries(term.where)
      .map(([k, v]) => `${k} in [${(v as string[]).join(", ")}]`)
      .join(" AND ");
  }
  if (term.under) return `under ${term.under}${term.party_type ? ` (${term.party_type})` : ""}`;
  if (term.group) return `group ${term.group}`;
  if (term.parties) return `parties [${term.parties.join(", ")}]`;
  if (term.self) return "self";
  return "(empty)";
}

/**
 * Blast radius before sending, with the freshness of everything it depends on beside it. A
 * broadcast to the wrong region is a mistake customers notice before monitoring does (§10).
 */
export function AudiencePreviewPanel({
  audience,
  channels,
}: {
  audience: AudienceExpression;
  channels?: string[];
}) {
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const empty = (audience.include ?? []).length === 0;

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setPreview(await audiences.audiences_Preview({ audience, channels, sampleSize: 200 }));
    } catch (e) {
      setPreview(null);
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button type="button" className="ghost" onClick={run} disabled={empty || busy}>
        {busy ? "Resolving…" : "Preview audience"}
      </button>

      {error && <div style={{ marginTop: 10 }}><Alert kind="error">{error}</Alert></div>}

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 22, fontWeight: 650 }}>
            {preview.count} {preview.count === 1 ? "party" : "parties"}
          </div>

          {preview.freshness?.map((f) => (
            <div key={f.key} style={{ marginTop: 6 }}>
              <Chip kind={f.isStale ? "warn" : undefined}>{f.label}</Chip>
            </div>
          ))}

          {preview.warnings?.map((w, i) => (
            <div key={i} style={{ marginTop: 8 }}>
              <Alert kind="warn">{w.message}</Alert>
            </div>
          ))}

          <div style={{ marginTop: 10, maxHeight: 220, overflowY: "auto" }}>
            <table>
              <thead>
                <tr><th>Key</th><th>Name</th><th>Type</th><th>Channels</th></tr>
              </thead>
              <tbody>
                {preview.sample?.map((p) => (
                  <tr key={p.key}>
                    <td className="mono">{p.key}</td>
                    <td>{p.name}</td>
                    <td>{p.type}</td>
                    <td>{p.channels?.map((c) => <Chip key={c}>{c}</Chip>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
